import type { ResearchAdoptionCard } from "./research-adoption";

const prohibitedCopy: ResearchAdoptionCard["prohibitedCopy"] = [
  "proprietary_data",
  "proprietary_formula",
  "visual_design",
  "brand_assets",
];

type CardInput = Omit<ResearchAdoptionCard, "schemaVersion" | "prohibitedCopy" | "scope" | "reviewedAt">;

function card(input: CardInput): ResearchAdoptionCard {
  return {
    schemaVersion: "tradvera.research-adoption.v1",
    prohibitedCopy,
    scope: "us_equities_eod_long_cash_paper",
    reviewedAt: "2026-08-06T00:00:00.000Z",
    ...input,
  };
}

export const MARKET_RESEARCH_ADOPTION_CARDS: ResearchAdoptionCard[] = [
  card({
    id: "research.koyfin-candidate-workspace.v1",
    sourcePlatform: "Koyfin",
    mechanism: "Versioned candidate workspace with saved screens and why-now reasons",
    sourceUrls: ["https://www.koyfin.com/help/topic/functionality/", "https://www.koyfin.com/help/faq/where-do-you-get-your-data/"],
    benefit: "Makes candidate discovery reproducible without turning a screen into a recommendation.",
    integrationContract: "tradvera.screen-registry.v1",
    decisionStages: ["DISCOVERY", "L1"], formulaIds: [],
    testIds: ["screen-reproducibility", "reason-code-completeness"],
    explainIds: ["candidate.why_surfaced"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.finviz-screen-reasons.v1",
    sourcePlatform: "Finviz",
    mechanism: "Fundamental and technical screen presets with contextual flags",
    sourceUrls: ["https://finviz.com/help/screener"],
    benefit: "Adds fast discovery while preserving reason codes and no-CTA boundaries.",
    integrationContract: "tradvera.screen-registry.v1",
    decisionStages: ["DISCOVERY", "L1"], formulaIds: [],
    testIds: ["screen-pit-universe", "screen-no-ranking"],
    explainIds: ["candidate.screen_flags"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.tikr-expectations.v1",
    sourcePlatform: "TIKR",
    mechanism: "Actual-consensus-revision and earnings-quality observatory",
    sourceUrls: ["https://support.tikr.com/hc/en-us/articles/39071375390235-How-do-I-use-TIKR-s-Estimates-feature"],
    benefit: "Separates expectations, revisions, basis and coverage from price direction claims.",
    integrationContract: "tradvera.expectations-observatory.v1",
    decisionStages: ["L4"],
    formulaIds: ["expectations.surprise_pct", "expectations.revision_pct", "quality.accrual_ratio"],
    testIds: ["estimate-basis-coverage", "revision-vintage"],
    explainIds: ["expectations.surprise", "expectations.revision"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.simplywallst-constellation.v1",
    sourcePlatform: "Simply Wall St",
    mechanism: "Independent company evidence dimensions with drill-down",
    sourceUrls: ["https://support.simplywall.st/hc/en-us/articles/360001740916-How-does-the-Snowflake-work"],
    benefit: "Keeps valuation, health, quality and future evidence separate instead of hiding conflict in one rating.",
    integrationContract: "tradvera.company-constellation.v1",
    decisionStages: ["L4"], formulaIds: [],
    testIds: ["dimension-drilldown", "no-opaque-rating"],
    explainIds: ["company.constellation"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.quartr-source-event-graph.v1",
    sourcePlatform: "Quartr",
    mechanism: "Company-event-artifact graph and narrative diff with exact-source jumps",
    sourceUrls: ["https://quartr.com/docs/data-overview", "https://quartr.com/features/global-search"],
    benefit: "Joins filings, transcripts and slides into traceable claims and changes.",
    integrationContract: "tradvera.primary-source-event-graph.v1",
    decisionStages: ["L1", "L4"], formulaIds: [],
    testIds: ["event-artifact-lineage", "narrative-diff-determinism"],
    explainIds: ["source.event_graph", "source.narrative_diff"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.openbb-standard-models.v1",
    sourcePlatform: "OpenBB",
    mechanism: "Provider-neutral standard models and declarative widget contracts",
    sourceUrls: ["https://docs.openbb.co/odp/python/developer/standardization"],
    benefit: "Allows provider replacement without rewriting decision logic or screens.",
    integrationContract: "tradvera.canonical-market.v1",
    decisionStages: ["G0", "L1"], formulaIds: [],
    testIds: ["provider-swap-contract", "canonical-units-null-semantics"],
    explainIds: ["source.provider_contract"],
    rightsPosture: "documented_public_terms", costState: "zero_official", status: "specified",
  }),
  card({
    id: "research.stockcharts-breadth.v1",
    sourcePlatform: "StockCharts ChartSchool",
    mechanism: "Market breadth and benchmark-relative strength taxonomy",
    sourceUrls: ["https://chartschool.stockcharts.com/table-of-contents/market-indicators"],
    benefit: "Tests whether participation confirms an index or stock move before thesis review.",
    integrationContract: "tradvera.market-health-passport.v1",
    decisionStages: ["L2"],
    formulaIds: ["market.advance_decline_pct", "market.pct_above_ma", "market.price_relative"],
    testIds: ["breadth-pit-universe", "relative-strength-golden"],
    explainIds: ["market.breadth.advance_decline", "market.relative_strength.price_relative"],
    rightsPosture: "documented_public_terms", costState: "zero_official", status: "specified",
  }),
  card({
    id: "research.barchart-market-health.v1",
    sourcePlatform: "Barchart",
    mechanism: "Market health passport using breadth by count, volume, highs and moving averages",
    sourceUrls: ["https://www.barchart.com/stocks/momentum", "https://www.barchart.com/solutions/exchange-fees"],
    benefit: "Adds interpretable market participation context while keeping data rights explicit.",
    integrationContract: "tradvera.market-health-passport.v1",
    decisionStages: ["L2"],
    formulaIds: ["market.advance_decline_pct", "market.new_high_low_pct", "market.pct_above_ma"],
    testIds: ["breadth-coverage", "exchange-rights-gate"],
    explainIds: ["market.health_passport"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.fred-alfred-vintage.v1",
    sourcePlatform: "FRED/ALFRED",
    mechanism: "Macro observation vintages and known-at timestamps",
    sourceUrls: ["https://fred.stlouisfed.org/docs/api/fred/realtime_period.html"],
    benefit: "Prevents future revised macro values from leaking into historical decisions.",
    integrationContract: "tradvera.macro-vintage-passport.v1",
    decisionStages: ["L1", "L2", "L4"], formulaIds: [],
    testIds: ["macro-future-revision-leakage", "macro-known-at"],
    explainIds: ["macro.vintage_passport"],
    rightsPosture: "official_public", costState: "zero_official", status: "specified",
  }),
  card({
    id: "research.ravenpack-event-semantics.v1",
    sourcePlatform: "RavenPack",
    mechanism: "Entity/event taxonomy with relevance, novelty and sentiment kept separate",
    sourceUrls: ["https://www.ravenpack.com/products/edge/data/news-analytics"],
    benefit: "Prevents relay duplication and stops sentiment from masquerading as materiality or price direction.",
    integrationContract: "tradvera.event-intelligence.v1",
    decisionStages: ["L1", "L4", "G5"], formulaIds: [],
    testIds: ["event-signal-separation", "relay-dedup"],
    explainIds: ["event.relevance", "event.novelty", "event.sentiment", "event.materiality"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.benzinga-event-transport.v1",
    sourcePlatform: "Benzinga",
    mechanism: "Created-updated-deleted event transport with cursor and replay",
    sourceUrls: ["https://docs.benzinga.com/ws-reference/overview"],
    benefit: "Propagates corrections and deletions idempotently to dossiers and alerts.",
    integrationContract: "tradvera.event-intelligence.v1",
    decisionStages: ["L1", "L4"], formulaIds: ["expectations.surprise_pct"],
    testIds: ["event-idempotency", "event-delete-propagation"],
    explainIds: ["event.transport_state"],
    rightsPosture: "commercial_review_required", costState: "financial_approval_required", status: "benchmark_only",
  }),
  card({
    id: "research.quantconnect-reality-model.v1",
    sourcePlatform: "QuantConnect/LEAN",
    mechanism: "Separate signal rules from fills, costs, corporate actions and warm-up",
    sourceUrls: ["https://www.quantconnect.com/docs/v2/writing-algorithms/reality-modeling/trade-fills/key-concepts"],
    benefit: "Prevents optimistic backtests and preserves the current EOD next-tick paper boundary.",
    integrationContract: "tradvera.reality-model-manifest.v1",
    decisionStages: ["G6"], formulaIds: [],
    testIds: ["next-tick-fill", "cost-monotonicity", "corporate-action-replay"],
    explainIds: ["strategy.reality_model"],
    rightsPosture: "documented_public_terms", costState: "zero_official", status: "specified",
  }),
];
